import { Helmet } from 'react-helmet-async'
import { FadeIn } from '@components/animations/FadeIn/FadeIn'
import { MapPin, TrendingUp, Target, Globe } from 'lucide-react'

const states = [
  { name: 'Maharashtra', status: 'active', projects: '200+', offices: 'Pune, Mumbai, Nagpur' },
  { name: 'Karnataka', status: 'active', projects: '50+', offices: 'Bangalore, Mysore' },
  { name: 'Tamil Nadu', status: 'active', projects: '30+', offices: 'Chennai, Coimbatore' },
  { name: 'Telangana', status: 'active', projects: '40+', offices: 'Hyderabad' },
  { name: 'Gujarat', status: 'active', projects: '35+', offices: 'Ahmedabad, Gandhinagar' },
  { name: 'Madhya Pradesh', status: 'active', projects: '25+', offices: 'Bhopal, Indore' },
  { name: 'Rajasthan', status: 'planned', projects: 'Coming Soon', offices: 'Jaipur (Planned)' },
  { name: 'Uttar Pradesh', status: 'planned', projects: 'Coming Soon', offices: 'Lucknow (Planned)' },
  { name: 'Delhi NCR', status: 'planned', projects: 'Coming Soon', offices: 'New Delhi (Planned)' },
  { name: 'West Bengal', status: 'planned', projects: 'Coming Soon', offices: 'Kolkata (Planned)' },
]

const ExpansionMap = () => {
  return (
    <>
      <Helmet>
        <title>Pan India Expansion | Eonixa Dynamics</title>
        <meta name="description" content="Our Pan-India presence - 6 states, 15+ offices, 400+ projects. Expanding to 10+ states by 2027." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-32 pb-20">
        <div className="section-container">
          <FadeIn>
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold text-gray-900">Pan India Expansion</h1>
              <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                From Maharashtra to Pan-India. Our strategic expansion across states
                to serve government departments and institutions nationwide.
              </p>
            </div>
          </FadeIn>

          {/* Expansion Stats */}
          <FadeIn delay={0.2}>
            <div className="grid gap-8 sm:grid-cols-4 mb-16">
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <MapPin className="mx-auto mb-4 h-8 w-8 text-primary-600" />
                <p className="text-4xl font-bold text-gray-900">6</p>
                <p className="mt-2 text-gray-600">Active States</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <TrendingUp className="mx-auto mb-4 h-8 w-8 text-primary-600" />
                <p className="text-4xl font-bold text-gray-900">400+</p>
                <p className="mt-2 text-gray-600">Projects Delivered</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <Target className="mx-auto mb-4 h-8 w-8 text-primary-600" />
                <p className="text-4xl font-bold text-gray-900">10</p>
                <p className="mt-2 text-gray-600">Target States by 2027</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <Globe className="mx-auto mb-4 h-8 w-8 text-primary-600" />
                <p className="text-4xl font-bold text-gray-900">15+</p>
                <p className="mt-2 text-gray-600">Office Locations</p>
              </div>
            </div>
          </FadeIn>

          {/* State-wise Presence */}
          <FadeIn delay={0.4}>
            <div className="rounded-2xl bg-white p-8 shadow-soft">
              <h2 className="mb-6 text-2xl font-bold text-gray-900">State-wise Presence</h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {states.map((state) => (
                  <div
                    key={state.name}
                    className={`rounded-xl p-6 border-2 ${
                      state.status === 'active'
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-gray-900">{state.name}</h3>
                      <span
                        className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          state.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {state.status === 'active' ? 'Active' : 'Planned'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">Projects:</span> {state.projects}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Offices:</span> {state.offices}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.6} className="mt-16">
            <div className="rounded-2xl bg-gradient-to-r from-primary-900 to-primary-700 p-12 text-center text-white">
              <h2 className="mb-4 text-2xl font-bold">Expansion Roadmap 2025-2027</h2>
              <p className="mx-auto max-w-2xl text-gray-200 mb-8">
                We're expanding to 4 more states by 2027, with plans to establish
                regional offices in Delhi NCR, Rajasthan, Uttar Pradesh, and West Bengal.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="rounded-xl bg-white/10 px-6 py-4 backdrop-blur-sm">
                  <p className="text-2xl font-bold">2025</p>
                  <p className="text-sm text-gray-300">Rajasthan & UP</p>
                </div>
                <div className="rounded-xl bg-white/10 px-6 py-4 backdrop-blur-sm">
                  <p className="text-2xl font-bold">2026</p>
                  <p className="text-sm text-gray-300">Delhi NCR</p>
                </div>
                <div className="rounded-xl bg-white/10 px-6 py-4 backdrop-blur-sm">
                  <p className="text-2xl font-bold">2027</p>
                  <p className="text-sm text-gray-300">West Bengal</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </>
  )
}

export default ExpansionMap
